import { AUTH_ROLES } from "src/constants";

const DEVELOPER_ROUTE_NAMES = new Set(["DeveloperPage", "Level1Form"]);
const FARMER_ROUTE_NAMES = new Set(["FarmerPage", "FarmerForm"]);

const DEVELOPER_PATH_PREFIXES = ["/developer", "/project-application"];
const FARMER_PATH_PREFIXES = ["/farmer", "/farmer-form"];
const INVESTOR_PATH_PREFIXES = ["/projects-investment"];

const SPONSORED_ROLES = new Set([AUTH_ROLES.DEVELOPER, AUTH_ROLES.FARMER]);

const hasRoleValue = (role) => role !== null && role !== undefined;

const normalizePath = (value) => {
  const raw = String(value || "").split(/[?#]/)[0];
  if (!raw) return "";
  return raw.startsWith("/") ? raw : `/${raw}`;
};

const matchesPathPrefix = (path, prefixes) => {
  const normalizedPath = normalizePath(path);
  return prefixes.some(
    (prefix) =>
      normalizedPath === prefix || normalizedPath.startsWith(`${prefix}/`)
  );
};

export const normalizeAuthRole = (role) => {
  if (!hasRoleValue(role)) {
    return null;
  }
  const normalized = Number(role);
  return Number.isFinite(normalized) ? normalized : null;
};

export const authRoleMatches = (currentRole, expectedRole) => {
  const normalizedExpected = normalizeAuthRole(expectedRole);
  if (normalizedExpected === null) {
    return true;
  }
  const normalizedCurrent = normalizeAuthRole(currentRole);
  return normalizedCurrent === normalizedExpected;
};

export const hasAuthenticatedRole = (state, expectedRole) => {
  return Boolean(state?.isAuthenticated) &&
    authRoleMatches(state?.currentAccount?.role, expectedRole);
};

export const shouldDeferAuthenticationWatch = ({
  isLogging = false,
  connectingMetaKeep = false,
  isAuthenticated = false,
} = {}) =>
  Boolean(isLogging || (connectingMetaKeep && !isAuthenticated));

export const resolveDashboardPathForRole = (role) => {
  switch (normalizeAuthRole(role)) {
    case AUTH_ROLES.DEVELOPER:
      return "/developer";
    case AUTH_ROLES.FARMER:
      return "/farmer";
    case AUTH_ROLES.AUDITOR:
      return "/auditor";
    case AUTH_ROLES.ADMIN:
      return "/admin";
    case AUTH_ROLES.INVESTOR:
    default:
      return "/dashboard";
  }
};

export const resolveAuthRoleForRoute = (route, explicitRole = null) => {
  const normalizedExplicit = normalizeAuthRole(explicitRole);
  if (normalizedExplicit !== null) {
    return normalizedExplicit;
  }

  const name = String(route?.name || "");
  const path = typeof route === "string" ? route : route?.path;

  if (
    DEVELOPER_ROUTE_NAMES.has(name) ||
    matchesPathPrefix(path, DEVELOPER_PATH_PREFIXES)
  ) {
    return AUTH_ROLES.DEVELOPER;
  }

  if (
    FARMER_ROUTE_NAMES.has(name) ||
    matchesPathPrefix(path, FARMER_PATH_PREFIXES)
  ) {
    return AUTH_ROLES.FARMER;
  }

  if (matchesPathPrefix(path, INVESTOR_PATH_PREFIXES)) {
    return AUTH_ROLES.INVESTOR;
  }

  return null;
};

export const isSponsoredAuthRoute = (route) => {
  return SPONSORED_ROLES.has(resolveAuthRoleForRoute(route));
};
